import { useContext, useState, useEffect } from "react"
import { Field, useFormikContext } from "formik"
import { FormContext } from "../../context/formContext" // Import the form context
import { InputText as Input } from "primereact/inputtext"
import { useLazyQuery } from "@apollo/client"
import { gql } from "@apollo/client"
import { trimTextValue } from "./helpers/trimTextValue"
import styles from "../../styles/Form.module.css"

export default function TypeEnterpriseName(props) {
  const { field, isRequired, index, initialValue } = props
  const formik = useFormikContext()
  const form = useContext(FormContext) // Access form.editMode from context
  const [validMessage, setValidMessage] = useState(null)
  const [isChecking, setIsChecking] = useState(false)
  // Get enterpriseId directly from the context
  const enterprise_id = formik.values.enterprise_id // Here we get the enterprise_id from the context
  const IS_ENTERPRISE_NAME_UNIQUE = gql`
    query IsEnterpriseNameUnique(
      $enterprise_name: String!
      $enterprise_id: ID
    ) {
      isEnterpriseNameUnique(
        enterprise_name: $enterprise_name
        enterprise_id: $enterprise_id
      )
    }
  `

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (form.editMode && formik.values.enterprise_name === initialValue) {
      // If we are in edit mode and the name hasn't changed, don't validate
      setValidMessage(null)
    }
  }, [form.editMode, formik.values.enterprise_name, initialValue])

  // useLazyQuery to execute the query manually
  const [checkNameUnique, { error }] = useLazyQuery(IS_ENTERPRISE_NAME_UNIQUE, {
    fetchPolicy: "network-only",
    onCompleted: () => setIsChecking(false), // Reset validation state
  })

  const handleBlur = async (e) => {
    formik.handleBlur(e) // Run Formik's onBlur

    const currentName = formik.values.enterprise_name?.trim()
    if (!currentName || currentName === initialValue) {
      return // Don't validate if name hasn't changed or is empty
    }

    setIsChecking(true) // Start validation

    try {
      const response = await checkNameUnique({
        variables: {
          enterprise_id: form.editMode ? enterprise_id : null, // Use enterprise_id only if in edit mode
          enterprise_name: currentName, // Validate the base name
        },
      })

      const isUnique = response?.data?.isEnterpriseNameUnique

      if (!isUnique) {
        formik.setFieldError(
          "enterprise_name",
          "The enterprise name must be unique." // Message in English
        )
        setValidMessage(null)
      } else {
        // Clear uniqueness error
        if (
          formik.errors.enterprise_name ===
          "The enterprise name must be unique."
        ) {
          formik.setFieldError("enterprise_name", undefined)
        }
        setValidMessage("The enterprise name is valid.")
      }
    } catch (err) {
      console.error("Error verifying enterprise name uniqueness:", err)
      formik.setFieldError(
        "enterprise_name",
        "Error checking the enterprise name."
      )
      setValidMessage(null)
    } finally {
      setIsChecking(false)
    }
  }

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: "block", // Display handling is done in FieldBuilder
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              <Input
                name={formikField.name}
                id={formikField.name}
                value={formikField.value}
                onChange={(e) => formikField.onChange(trimTextValue(e))}
                disabled={isChecking}
                onBlur={handleBlur}
                autoComplete="off"
              />
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
              {isChecking && (
                <div className={styles.infoFieldMsg}>Checking uniqueness...</div>
              )}
              {error && (
                <div className={styles.errFieldMsg}>
                  Error checking the enterprise name.
                </div>
              )}
              {!isChecking && !meta.error && validMessage && (
                <div className={styles.validFieldMsg}>{validMessage}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}
